import type { MenuException, MenuOccurrence, MenuRecurrenceRule, MenuSchedule, MenuScheduleItem } from './menuTypes';

const DAY = 86_400_000;
const mod = (n: number, divisor: number) => ((n % divisor) + divisor) % divisor;
const MAX_DAY = Math.floor(Date.UTC(9999, 11, 31) / DAY);

/** Date-only arithmetic: no browser time zone or DST enters the recurrence. */
export function menuDateOrdinal(value: string): number {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('MENU_INVALID_DATE');
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) throw new Error('MENU_INVALID_DATE');
  return Math.floor(parsed.getTime() / DAY);
}
export function menuIsoDate(ordinal: number): string {
  if (!Number.isInteger(ordinal) || ordinal > MAX_DAY) throw new Error('MENU_INVALID_DATE');
  const value = new Date(ordinal * DAY).toISOString().slice(0, 10);
  menuDateOrdinal(value);
  return value;
}
export const menuAddDays = (date: string, days: number): string => menuIsoDate(menuDateOrdinal(date) + days);
export const menuWeekStart = (date: string): string => {
  const day = menuDateOrdinal(date);
  return menuIsoDate(day - mod(day + 4, 7));
};
export function menuDates(from: string, to: string, maxDays = 42): string[] {
  const start = menuDateOrdinal(from), end = menuDateOrdinal(to);
  if (end < start || end - start + 1 > maxDays) throw new Error('MENU_INVALID_RANGE');
  return Array.from({ length: end - start + 1 }, (_, index) => menuIsoDate(start + index));
}
function assertRule(rule: MenuRecurrenceRule) {
  if (!['NONE', 'WEEKLY'].includes(rule.frequency) || !Number.isInteger(rule.interval) || rule.interval < 1 || rule.interval > 52
    || !Array.isArray(rule.daysOfWeek) || rule.daysOfWeek.some((day) => !Number.isInteger(day) || day < 0 || day > 6)
    || (rule.frequency === 'WEEKLY' && !rule.daysOfWeek.length)) throw new Error('MENU_INVALID_RECURRENCE');
  const start = menuDateOrdinal(rule.startsOn);
  menuDateOrdinal(rule.anchorDate);
  if (rule.endsOn !== null && menuDateOrdinal(rule.endsOn) < start) throw new Error('MENU_INVALID_RECURRENCE');
}
export function menuScheduledOn(schedule: MenuSchedule, date: string): boolean {
  if (!schedule.active) return false;
  const day = menuDateOrdinal(date), rule = schedule.recurrence;
  assertRule(rule);
  if (day < menuDateOrdinal(rule.startsOn) || (rule.endsOn && day > menuDateOrdinal(rule.endsOn))) return false;
  if (rule.frequency === 'NONE') return date === rule.startsOn;
  const weekday = mod(day + 4, 7);
  const anchorWeek = menuDateOrdinal(menuWeekStart(rule.anchorDate));
  return rule.daysOfWeek.includes(weekday) && mod(Math.floor((day - anchorWeek) / 7), rule.interval) === 0;
}
function effectiveItems(schedule: MenuSchedule, exception: MenuException | undefined, date: string): { shift: string; items: MenuScheduleItem[]; exception: boolean } | null {
  if (!menuScheduledOn(schedule, date)) return null;
  if (exception?.cancelled) return null;
  return { shift: exception?.shift ?? schedule.shift, items: exception?.items ?? schedule.items, exception: Boolean(exception) };
}
export function menuEffectiveItems(schedule: MenuSchedule, exceptions: MenuException[], date: string) {
  return effectiveItems(schedule, exceptions.find((entry) => entry.restaurantId === schedule.restaurantId && entry.scheduleId === schedule.id && entry.originalDate === date), date);
}
const exceptionKey = (restaurantId: string, scheduleId: string, date: string) => `${restaurantId}\0${scheduleId}\0${date}`;
function exceptionLookup(exceptions: MenuException[]) {
  const index = new Map<string, MenuException>();
  for (const entry of exceptions) {
    const key = exceptionKey(entry.restaurantId, entry.scheduleId, entry.originalDate);
    if (index.has(key)) throw new Error('MENU_DUPLICATE_EXCEPTION');
    index.set(key, entry);
  }
  return index;
}
export function menuOccurrences(schedules: MenuSchedule[], exceptions: MenuException[], from: string, to: string): MenuOccurrence[] {
  const index = exceptionLookup(exceptions);
  return menuDates(from, to).flatMap((date) => schedules.flatMap((schedule) => {
    const result = effectiveItems(schedule, index.get(exceptionKey(schedule.restaurantId, schedule.id, date)), date);
    return result ? [{
      id: `${schedule.id}__${date}`, scheduleId: schedule.id, date, shift: result.shift,
      items: [...result.items].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)),
      revision: schedule.revision, recurring: schedule.recurrence.frequency === 'WEEKLY', exception: result.exception, canEdit: false,
    }] : [];
  }));
}

type Progression = { first: number; period: number; low: number; high: number };
function progressions(schedule: MenuSchedule): Progression[] {
  assertRule(schedule.recurrence);
  const rule = schedule.recurrence, low = menuDateOrdinal(rule.startsOn), high = rule.endsOn ? menuDateOrdinal(rule.endsOn) : MAX_DAY;
  if (!schedule.active || low > high) return [];
  if (rule.frequency === 'NONE') return [{ first: low, period: 0, low, high: low }];
  const first = menuDateOrdinal(menuWeekStart(rule.anchorDate));
  return rule.daysOfWeek.map((day) => ({ first: first + day, period: 7 * rule.interval, low, high }));
}
function gcd(a: number, b: number): number { while (b) [a, b] = [b, a % b]; return a; }
function inverse(a: number, modulus: number): number {
  let [oldR, r, oldS, s] = [a, modulus, 1, 0];
  while (r) { const q = Math.floor(oldR / r); [oldR, r] = [r, oldR - q * r]; [oldS, s] = [s, oldS - q * s]; }
  return mod(oldS, modulus);
}
/** Exact intersection of arithmetic progressions, including distant infinite rules. */
function intersection(a: Progression, b: Progression): { first: number; step: number; high: number } | null {
  const low = Math.max(a.low, b.low), high = Math.min(a.high, b.high);
  if (low > high) return null;
  if (!a.period || !b.period) {
    const fixed = a.period ? b : a, other = a.period ? a : b, first = fixed.first;
    return first >= low && first <= high && (!other.period ? first === other.first : mod(first - other.first, other.period) === 0) ? { first, step: 0, high } : null;
  }
  const divisor = gcd(a.period, b.period), difference = b.first - a.first;
  if (mod(difference, divisor)) return null;
  const reduced = b.period / divisor;
  const multiplier = reduced === 1 ? 0 : mod((difference / divisor) * inverse(a.period / divisor, reduced), reduced);
  const step = a.period * reduced;
  let first = a.first + a.period * multiplier;
  first += Math.ceil((low - first) / step) * step;
  return first <= high ? { first, step, high } : null;
}
function firstBaseIntersection(a: MenuSchedule, b: MenuSchedule, omitted: Set<number>): number | null {
  let earliest: number | null = null;
  for (const left of progressions(a)) for (const right of progressions(b)) {
    const found = intersection(left, right);
    if (!found) continue;
    let day = found.first;
    // Each skipped occurrence consumes one finite exception. No arbitrary horizon.
    while (day <= found.high && omitted.has(day)) { if (!found.step) { day = found.high + 1; break; } day += found.step; }
    if (day <= found.high && (earliest === null || day < earliest)) earliest = day;
  }
  return earliest;
}
/** Exact first occurrence of the base rule; null for a finite range with no selected weekday. */
export function menuFirstDate(schedule: MenuSchedule): string | null {
  const day = firstBaseIntersection(schedule, schedule, new Set());
  return day === null ? null : menuIsoDate(day);
}
export interface MenuConflict { date: string; shift: string; recipeId: string }
export function findMenuConflict(schedules: MenuSchedule[], exceptions: MenuException[]): MenuConflict | null {
  const active = schedules.filter((schedule) => schedule.active);
  const byId = new Map(active.map((schedule) => [`${schedule.restaurantId}\0${schedule.id}`, schedule]));
  const validExceptions = exceptions.filter((exception) => {
    const schedule = byId.get(`${exception.restaurantId}\0${exception.scheduleId}`);
    return Boolean(schedule && menuScheduledOn(schedule, exception.originalDate));
  });
  const exceptionIndex = exceptionLookup(validExceptions);
  const omitted = new Map<string, Set<number>>();
  for (const exception of validExceptions) {
    const key = `${exception.restaurantId}\0${exception.scheduleId}`;
    if (!omitted.has(key)) omitted.set(key, new Set());
    omitted.get(key)!.add(menuDateOrdinal(exception.originalDate));
  }
  // Finite overrides are evaluated with all effective rows for their actual date.
  for (const date of new Set(validExceptions.map((exception) => exception.originalDate))) {
    const seen = new Set<string>();
    for (const schedule of active) {
      const result = effectiveItems(schedule, exceptionIndex.get(exceptionKey(schedule.restaurantId, schedule.id, date)), date);
      if (!result) continue;
      for (const item of result.items) {
        const key = `${schedule.restaurantId}\0${result.shift}\0${item.recipeId}`;
        if (seen.has(key)) return { date, shift: result.shift, recipeId: item.recipeId };
        seen.add(key);
      }
    }
  }
  // Remaining dates are arithmetic progressions with finite dates removed.
  const recipeIds = new Map(active.map((schedule) => [schedule, new Set(schedule.items.map((item) => item.recipeId))]));
  for (let i = 0; i < active.length; i++) {
    const left = active[i], leftOmitted = omitted.get(`${left.restaurantId}\0${left.id}`) ?? new Set<number>();
    if (recipeIds.get(left)!.size !== left.items.length) {
      const date = firstBaseIntersection(left, left, leftOmitted);
      const repeated = left.items.find((item, index) => left.items.findIndex((entry) => entry.recipeId === item.recipeId) !== index)!;
      if (date !== null) return { date: menuIsoDate(date), shift: left.shift, recipeId: repeated.recipeId };
    }
    for (let j = i + 1; j < active.length; j++) {
      const right = active[j];
      if (left.restaurantId !== right.restaurantId || left.shift !== right.shift) continue;
      const recipeId = right.items.find((item) => recipeIds.get(left)!.has(item.recipeId))?.recipeId;
      if (!recipeId) continue;
      const excluded = new Set([...leftOmitted, ...(omitted.get(`${right.restaurantId}\0${right.id}`) ?? [])]);
      const date = firstBaseIntersection(left, right, excluded);
      if (date !== null) return { date: menuIsoDate(date), shift: left.shift, recipeId };
    }
  }
  return null;
}
